import { Group, Switch, Title } from '@mantine/core';
import {
  DEFAULT_SEARCH_COUNT,
  Filter,
  formatSearchQuery,
  Operator,
  parseSearchRequest,
  SearchRequest,
  SortRule,
} from '@medplum/core';
import { Encounter, Resource, UserConfiguration } from '@medplum/fhirtypes';
import {
  Document,
  Loading,
  useMedplum,
} from '@medplum/react';
import { FC, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useResourceType } from './resource/useResourceType';
import { SearchControl } from '@/components/medplum-react/SearchControl/SearchControl';
import { useTranslation } from 'react-i18next';
import { renderEncounterCell } from './ainigma/patient/encounters-tab/renderEncounterCell';
import { getResourceUrl } from '@utils/resource.utils';


interface SearchPageProps {
  searchRequest?: SearchRequest;
  onResourceClick?: (resource: Resource) => void;
}
// -----------------------------------------------------------------------------
export const SearchPage: FC<SearchPageProps> = ({
  searchRequest,
  onResourceClick,
}): JSX.Element => {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(['common']);
  const { t: tEncounters } = useTranslation(['encounters']);
  

  const [baseSearch, setBaseSearch] = useState<SearchRequest>();
  const [onlyInProgress, setOnlyInProgress] = useState(false);

  useEffect(() => {
    const populate = (input: SearchRequest): SearchRequest => {
      const populated = addSearchValues(input, medplum.getUserConfiguration());
      saveLastSearch(populated);
      return populated;
    };

    if (searchRequest) {
      setBaseSearch(populate(searchRequest));
    } else {
      const parsedSearch = parseSearchRequest(
        location.pathname + location.search
      );
      const populated = populate(parsedSearch);

      if (
        location.pathname === `/${populated.resourceType}` &&
        location.search === formatSearchQuery(populated)
      ) {
        setBaseSearch(populated);
      } else {
        navigate(`/${populated.resourceType}${formatSearchQuery(populated)}`);
      }
    }
  }, [medplum, navigate, location, searchRequest]);

  useResourceType(baseSearch?.resourceType, {
    onInvalidResourceType: () => navigate('..'),
  });

  const search = useMemo(() => {
    if (!baseSearch) {
      return undefined;
    }

    const newFilters = [
      ...(baseSearch.filters || []),
      ...(onlyInProgress && baseSearch.resourceType === 'Encounter'
        ? [{ code: 'status', operator: Operator.EQUALS, value: 'in-progress' }]
        : []),
    ];

    return {
      ...baseSearch,
      filters: newFilters,
    };
  }, [baseSearch, onlyInProgress]);

  if (!search?.resourceType || !search.fields || search.fields.length === 0) {
    return <Loading />;
  }

  return (
    <Document>
      <Group justify="space-between">
        {search?.resourceType === 'Encounter' && (
          <Title>
            {t('layout.sidebar.encounters', { ns: 'common' })}
          </Title>
        )}
        {search?.resourceType === 'Encounter' && (
          <Switch
            label={t('inProgress', { ns: 'common' })}
            checked={onlyInProgress}
            onChange={(event) => setOnlyInProgress(event.currentTarget.checked)}
          />
        )}
      </Group>

      {/* <Paper shadow="xs" m="md" p="xs" className={classes.paper}> */}
      <SearchControl
        checkboxesEnabled={false}
        hideFilters
        hideToolbar
        search={search}
        onClick={(e) => {
          if (onResourceClick) {
            onResourceClick(e.resource);
            return;
          }

          return navigate(getResourceUrl(e.resource));
        }}
        onAuxClick={(e) => window.open(getResourceUrl(e.resource), '_blank')}
        onNew={() => {
          navigate(`/${search.resourceType}/new`);
        }}
        onChange={(e) => {
          if (searchRequest) {
            setBaseSearch({
              ...searchRequest,
              ...e.definition,
            });

            return;
          }
          navigate(`/${search.resourceType}${formatSearchQuery(e.definition)}`);
        }}
        renderCell={(resource, field) => {
          if (resource.resourceType === 'Encounter') {
            return renderEncounterCell(resource as Encounter, field, tEncounters);
          }
          return undefined;
        }}
      />
      {/* </Paper> */}
    </Document>
  );
};

// -----------------------------------------------------------------------------
const addSearchValues = (
  search: SearchRequest,
  config: UserConfiguration | undefined
): SearchRequest => {
  const resourceType = search.resourceType || getDefaultResourceType(config);
  const fields = search.fields ?? ['_id', '_lastUpdated'];
  const filters =
    search.filters ??
    (!search.resourceType ? getDefaultFilters(resourceType) : undefined);
  const sortRules = search.sortRules ?? getDefaultSortRules(resourceType);
  const offset = search.offset ?? 0;
  const count = search.count ?? DEFAULT_SEARCH_COUNT;

  return {
    ...search,
    resourceType,
    fields,
    filters,
    sortRules,
    offset,
    count,
  };
};

// -----------------------------------------------------------------------------
const getDefaultResourceType = (
  config: UserConfiguration | undefined
): string => {
  return (
    localStorage.getItem('defaultResourceType') ??
    config?.option?.find((o) => o.id === 'defaultResourceType')?.valueString ??
    'Task'
  );
};

// -----------------------------------------------------------------------------
const getDefaultFilters = (resourceType: string): Filter[] | undefined => {
  return getLastSearch(resourceType)?.filters;
};

// -----------------------------------------------------------------------------
const getDefaultSortRules = (resourceType: string): SortRule[] => {
  const lastSearch = getLastSearch(resourceType);
  if (lastSearch?.sortRules) {
    return lastSearch.sortRules;
  }
  return [{ code: '_lastUpdated', descending: true }];
};

// -----------------------------------------------------------------------------
const getLastSearch = (resourceType: string): SearchRequest | undefined => {
  const value = localStorage.getItem(resourceType + '-defaultSearch');
  return value ? (JSON.parse(value) as SearchRequest) : undefined;
};

// -----------------------------------------------------------------------------
const saveLastSearch = (search: SearchRequest): void => {
  localStorage.setItem('defaultResourceType', search.resourceType);
  localStorage.setItem(
    search.resourceType + '-defaultSearch',
    JSON.stringify(search)
  );
};
